import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { BOOKS, characterPath, compendiumPath } from "@/config/site";
import type { CompendiumKind } from "@/data/compendium";
import { splitMentions, type LinkableEntry } from "@/data/compendium-links";
import { loadCharacterLinks } from "@/data/load-character-links";
import { loadCompendium } from "@/data/load-compendium";

const NOTHING: readonly LinkableEntry[] = [];

interface CompendiumTextProps {
  text: string;
  bookId: string | undefined;
  /** The entry whose own page this is, so it does not link to itself. */
  skip?: { id: string; kind: CompendiumKind };
  /** Open links in a new tab, as in the player, so the story keeps its place. */
  newTab?: boolean;
}

/** Story text where everything in the compendium and everyone it names link to their pages. */
export function CompendiumText({ text, bookId, skip, newTab = false }: CompendiumTextProps) {
  const [entries, setEntries] = useState(NOTHING);
  const book = BOOKS.find((item) => item.id === bookId);

  useEffect(() => {
    if (bookId !== undefined) {
      // Compendium first, so a name both share goes to its entry.
      void Promise.all([loadCompendium(bookId), loadCharacterLinks(bookId)])
        .then(([compendium, characters]) => setEntries([...compendium, ...characters]));
    }
  }, [bookId]);

  if (book === undefined) {
    return text;
  }
  return splitMentions(text, entries, skip).map((part, index) => (
    part.entry === undefined
      ? part.text
      : (
        <Link
          key={index}
          className="race-link"
          to={part.entry.kind === "character"
            ? characterPath(book, part.entry.id)
            : compendiumPath(book, part.entry.id, part.entry.kind)}
          {...(newTab ? { target: "_blank", rel: "noopener" } : {})}
          onClick={(event) => event.stopPropagation()}
        >
          {part.text}
        </Link>
      )
  ));
}
